/**
 * Neyra — Referral store.
 * Unique per-user referral code + link, earnings (reward only when referred
 * user completes a transaction), and tracking of who joined via a code.
 */
import { mirror } from "@/lib/syncStore";
import { SUPABASE_TABLES } from "@/lib/supabaseClient";

const fireMirror = (table, row) => { try { mirror(table, row); } catch {} };

const BUS_EVT = "neyra-referral-change";
const emit = () => window.dispatchEvent(new Event(BUS_EVT));

export const subscribeReferral = (cb) => {
    window.addEventListener(BUS_EVT, cb);
    return () => window.removeEventListener(BUS_EVT, cb);
};

const read = (k, fallback) => {
    try {
        const v = localStorage.getItem(k);
        return v ? JSON.parse(v) : fallback;
    } catch { return fallback; }
};
const write = (k, v) => { localStorage.setItem(k, JSON.stringify(v)); emit(); };

const K = (userId, s) => `neyra.referral.${s}.${userId}`;

/** Reward given once the referred user completes their first transaction. */
export const REFERRAL_REWARD_NGN = 1000;

/** Deterministic code derived from userId (short, uppercase, 7 chars). */
export const getReferralCode = (userId) => {
    if (!userId) return "";
    const seed = String(userId);
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
    // base36, uppercase, pad to 7 chars
    const base = h.toString(36).toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 5);
    return "NEYRA-" + (base || "MINT").padStart(5, "0").slice(-5);
};

export const getReferralLink = (userId) => {
    const origin =
        typeof window !== "undefined" && window.location
            ? window.location.origin
            : "https://neyra.app";
    return `${origin}/?ref=${encodeURIComponent(getReferralCode(userId))}`;
};

/* ---------------- Referred users tracking ---------------- */

/** Returns [{ userId, code, at, rewarded, amount? }, …] */
export const getReferrals = (userId) => read(K(userId, "list"), []);

/** Someone joined using my code → record as pending (no reward yet). */
export const recordReferral = (referrerId, referredUserId) => {
    if (!referrerId || !referredUserId) return;
    const list = getReferrals(referrerId);
    if (list.some((r) => r.userId === referredUserId)) return;
    const record = {
        userId: referredUserId,
        code: getReferralCode(referrerId),
        at: Date.now(),
        rewarded: false,
    };
    list.unshift(record);
    write(K(referrerId, "list"), list);
    fireMirror(SUPABASE_TABLES.REFERRALS, {
        id: `${referrerId}_${referredUserId}`,
        referrerId,
        referredUserId,
        code: record.code,
        at: record.at,
        rewarded: false,
    });
};

/**
 * Referred user completed a transaction — reward the referrer (once).
 * Amount is fixed via REFERRAL_REWARD_NGN and credited to referrer's available
 * wallet balance via monetizationStore.addRefund-style path.
 */
export const rewardIfFirstTxn = async (referredUserId, { addRefundFn, addNotificationFn }) => {
    // Scan all referrers to find who invited this user. O(users) but fine here.
    const usersRaw = localStorage.getItem("neara.users");
    if (!usersRaw) return;
    const users = JSON.parse(usersRaw);
    for (const u of users) {
        const list = getReferrals(u.id);
        const idx = list.findIndex((r) => r.userId === referredUserId && !r.rewarded);
        if (idx < 0) continue;
        list[idx] = {
            ...list[idx],
            rewarded: true,
            rewardedAt: Date.now(),
            amount: REFERRAL_REWARD_NGN,
        };
        write(K(u.id, "list"), list);
        fireMirror(SUPABASE_TABLES.REFERRALS, {
            id: `${u.id}_${referredUserId}`,
            referrerId: u.id,
            referredUserId,
            code: list[idx].code,
            rewarded: true,
            rewardedAt: list[idx].rewardedAt,
            amount: REFERRAL_REWARD_NGN,
        });
        if (addRefundFn) {
            addRefundFn(u.id, REFERRAL_REWARD_NGN, {
                title: "Referral reward",
            });
        }
        if (addNotificationFn) {
            addNotificationFn(u.id, {
                type: "refund",
                title: "Referral reward earned",
                body: `You earned ₦${REFERRAL_REWARD_NGN.toLocaleString()} — someone you invited completed their first transaction.`,
                link: "/referral",
            });
        }
        break;
    }
};

/** Total earnings across all successful referrals. */
export const getReferralEarnings = (userId) =>
    getReferrals(userId)
        .filter((r) => r.rewarded)
        .reduce((acc, r) => acc + (Number(r.amount) || 0), 0);
